import { useEffect, useState } from "react";
import api from "../../services/api";
import hiwStepSearch from "../../assets/design/hiw-step-01-search.png";
import hiwStepBrowse from "../../assets/design/hiw-step-02-browse.png";
import hiwStepConnect from "../../assets/design/hiw-step-03-connect.png";
import hiwStepHire from "../../assets/design/hiw-step-04-hire.png";
import hiwStepJourney from "../../assets/design/hiw-step-05-journey.png";
import "./HowItWorksSection.css";

const HIW_IMAGES = {
  search: hiwStepSearch,
  browse: hiwStepBrowse,
  connect: hiwStepConnect,
  hire: hiwStepHire,
  journey: hiwStepJourney,
};

function StepIcon({ name }) {
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.85" };
  if (name === "search") {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "browse") {
    return (
      <svg {...common}>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "connect") {
    return (
      <svg {...common}>
        <path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />
      </svg>
    );
  }
  if (name === "hire") {
    return (
      <svg {...common}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

const FALLBACK = {
  headline: "Get Care at Home in 5 Simple Steps",
  subheadline: "Find, compare and connect with care professionals directly through NEMA CareHub.",
  locationLabel: "Gurugram, Haryana",
  steps: [
    {
      id: "search",
      number: "01",
      title: "Search for the Care You Need",
      description: "Explore available care professionals based on your location, care requirements and preferences.",
      icon: "search",
      image: HIW_IMAGES.search,
    },
    {
      id: "browse",
      number: "02",
      title: "Browse Care Professional Profiles",
      description: "Review available profile information, experience details and listed rates before deciding whom to contact.",
      icon: "browse",
      image: HIW_IMAGES.browse,
    },
    {
      id: "connect",
      number: "03",
      title: "Connect and Choose Yourself",
      description: "Discuss your requirements directly with the care professional and decide whether they are the right fit for your needs.",
      icon: "connect",
      image: HIW_IMAGES.connect,
    },
    {
      id: "hire",
      number: "04",
      title: "Hire Directly and Pay Directly",
      description: "Agree on care arrangements, schedule and payment terms with your chosen professional. Pay the professional directly, subject to applicable platform terms and fees.",
      icon: "hire",
      image: HIW_IMAGES.hire,
    },
    {
      id: "journey",
      number: "05",
      title: "Begin Your Care Journey",
      description: "Start your agreed care arrangement with greater control over your hiring decision, schedule and terms.",
      icon: "journey",
      image: HIW_IMAGES.journey,
    },
  ],
};

export default function HowItWorksSection({ latitude, longitude, cityLabel }) {
  const [content, setContent] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getPublicHowItWorks({
          latitude,
          longitude,
          cityLabel,
        });
        const payload = res?.data && typeof res.data === "object" ? res.data : res;
        if (!cancelled && payload) {
          // Marketing copy + step visuals are FE-owned so API lag cannot revert the 5-step UI.
          setContent({
            ...FALLBACK,
            locationLabel: payload.locationLabel || cityLabel || FALLBACK.locationLabel,
          });
        }
      } catch {
        if (!cancelled) setContent((prev) => ({ ...prev, locationLabel: cityLabel || prev.locationLabel }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude, cityLabel]);

  return (
    <section id="how-it-works" className="landing-hiw" aria-busy={loading}>
      <div className="landing-hiw__head">
        <p className="landing-hiw__eyebrow">HOW IT WORKS</p>
        <h2>
          Get Care at Home
          <br />
          in <span>5 Simple Steps</span>
        </h2>
        <p className="landing-hiw__lead">{content.subheadline}</p>
        <span className="landing-hiw__accent" aria-hidden="true" />
      </div>

      <div className="landing-hiw__track">
        {content.steps.map((step, index) => (
          <div key={step.id} className="landing-hiw__col">
            <div className="landing-hiw__step-row" aria-hidden="true">
              <span className="landing-hiw__step">{step.number}</span>
              {index < content.steps.length - 1 ? (
                <span className="landing-hiw__steps-rail-line" />
              ) : null}
            </div>
            <article className="landing-hiw__card">
              <div className="landing-hiw__visual">
                <img src={step.image} alt="" className="landing-hiw__visual-img" />
              </div>
              <div className="landing-hiw__body">
                <div className="landing-hiw__icon-wrap" aria-hidden="true">
                  <StepIcon name={step.icon} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </article>
          </div>
        ))}
      </div>
    </section>
  );
}
